import React, { useCallback, useState } from "react";
import { Film, Heart, MessageSquare, Play, Search, Trash2 } from "lucide-react";

import Topbar from "../components/Topbar";
import { Drawer, Field, Section } from "../components/ui/Drawer";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  useToast,
} from "../components/ui/Feedback";
import { Avatar, FilterTabs, Pagination, useDebounced } from "../components/ui/Table";
import { useQuery } from "../lib/useQuery";
import {
  deleteComment,
  deletePost,
  fetchPostComments,
  fetchPosts,
  type ModPost,
  type ModerationFilter,
} from "../lib/api";

/**
 * Community moderation.
 *
 * The old page listed post text and a delete button, which made it blind to
 * most of what this community actually is — the app has been media-first since
 * the post_media migration, so a post that is a video with no caption rendered
 * here as an empty row. There was also no way to see or remove a comment,
 * meaning the one thing you most need to moderate quickly had no surface at
 * all.
 *
 * Deletion goes through `admin_delete_post` / `admin_delete_comment` rather
 * than a direct `.delete()`. Those capture the author, an excerpt and your
 * reason before the row goes, which is the difference between a moderation
 * decision you can explain later and one you can't.
 */

const PAGE_SIZE = 24;

const FILTERS: { key: ModerationFilter; label: string }[] = [
  { key: "all", label: "All posts" },
  { key: "media", label: "With media" },
  { key: "tutorials", label: "Tutorials" },
  { key: "text", label: "Text only" },
];

const dateTime = (value: string) =>
  new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type Pending =
  | { kind: "post"; post: ModPost }
  | { kind: "comment"; commentId: string; excerpt: string };

export default function Community() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ModerationFilter>("all");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<ModPost | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const debounced = useDebounced(search);

  const list = useQuery(
    () => fetchPosts({ search: debounced, filter, limit: PAGE_SIZE, offset }),
    [debounced, filter, offset]
  );

  const comments = useQuery(
    () => (open ? fetchPostComments(open.id) : Promise.resolve([])),
    [open?.id]
  );

  const rows = list.data?.rows ?? [];

  const confirmDelete = useCallback(
    async (reason: string) => {
      if (!pending) return;
      setBusy(true);
      try {
        if (pending.kind === "post") {
          await deletePost(pending.post.id, reason);
          toast.success("Post deleted");
          setOpen(null);
          list.refetch();
        } else {
          await deleteComment(pending.commentId, reason);
          toast.success("Comment deleted");
          comments.refetch();
          list.refetch();
        }
        setPending(null);
      } catch (err) {
        toast.failure(err);
      } finally {
        setBusy(false);
      }
    },
    [pending, toast, list, comments]
  );

  return (
    <div>
      <Topbar
        title="Community"
        subtitle={list.data ? `${list.data.total.toLocaleString()} posts` : undefined}
      />
      <div className="page-shell">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          <FilterTabs
            tabs={FILTERS}
            active={filter}
            onChange={(next) => {
              setFilter(next);
              setOffset(0);
            }}
          />
          <div style={{ position: "relative", minWidth: 240, flex: "0 1 320px" }}>
            <Search
              size={14}
              color="var(--text-muted)"
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              className="input"
              placeholder="Search post text…"
              style={{ paddingLeft: 34 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>
        </div>

        {list.error ? (
          <div className="card">
            <ErrorState error={list.error} onRetry={list.refetch} />
          </div>
        ) : (
          <>
            {list.loading && (
              <div className="card">
                <div style={{ color: "var(--text-muted)", fontSize: 13, padding: 20 }}>Loading…</div>
              </div>
            )}

            {!list.loading && rows.length === 0 && (
              <div className="card">
                <EmptyState message="No posts match this view" />
              </div>
            )}

            {/* A grid of cards rather than a table: these are mostly images,
                and a table row can't show one without collapsing it to a
                thumbnail nobody can judge. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 16,
              }}
            >
              {rows.map((post) => {
                const cover = post.post_media
                  ?.slice()
                  .sort((a, b) => a.position - b.position)[0];
                const isTutorial = post.topics?.includes("Tutorial");

                return (
                  <article
                    key={post.id}
                    className="card"
                    onClick={() => setOpen(post)}
                    style={{ padding: 0, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}
                  >
                    {cover && (
                      <div style={{ position: "relative", background: "#f2f2f7", aspectRatio: "4 / 3" }}>
                        <img
                          src={cover.thumbnail_url ?? cover.url}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                        {cover.kind === "video" && (
                          <span style={overlayPill}>
                            <Play size={10} fill="#fff" /> Video
                          </span>
                        )}
                        {post.media_count > 1 && (
                          <span style={{ ...overlayPill, left: "auto", right: 10 }}>
                            <Film size={10} /> {post.media_count}
                          </span>
                        )}
                      </div>
                    )}

                    <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar
                          name={post.profiles?.full_name ?? null}
                          url={post.profiles?.avatar_url}
                          seed={post.user_id}
                          size={28}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)" }}>
                            {post.profiles?.full_name ?? "Unknown"}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                            {dateTime(post.created_at)}
                          </div>
                        </div>
                        {isTutorial && (
                          <span className="badge badge-primary" style={{ fontSize: 10 }}>
                            Tutorial
                          </span>
                        )}
                      </div>

                      <p
                        style={{
                          fontSize: 13,
                          color: "var(--text-secondary)",
                          lineHeight: 1.55,
                          margin: 0,
                          flex: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.content || <em style={{ color: "var(--text-light)" }}>No caption</em>}
                      </p>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          fontSize: 11.5,
                          color: "var(--text-muted)",
                          fontWeight: 650,
                        }}
                      >
                        <span style={inline}><Heart size={12} /> {post.likes_count}</span>
                        <span style={inline}><MessageSquare size={12} /> {post.comments_count}</span>
                        <button
                          className="btn btn-sm btn-danger"
                          style={{ border: "none", marginLeft: "auto" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPending({ kind: "post", post });
                          }}
                          title="Delete post"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <Pagination
              offset={offset}
              limit={PAGE_SIZE}
              total={list.data?.total ?? 0}
              onChange={setOffset}
              busy={list.fetching}
            />
          </>
        )}
      </div>

      <Drawer
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.profiles?.full_name ?? "Post"}
        subtitle={open ? dateTime(open.created_at) : undefined}
        width={560}
        footer={
          open ? (
            <button
              className="btn btn-sm btn-danger"
              style={{ border: "none" }}
              onClick={() => setPending({ kind: "post", post: open })}
            >
              <Trash2 size={13} /> Delete this post
            </button>
          ) : null
        }
      >
        {open && (
          <>
            {open.post_media?.length > 0 && (
              <Section title={`Media (${open.post_media.length})`}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                  {open.post_media
                    .slice()
                    .sort((a, b) => a.position - b.position)
                    .map((media) => (
                      <a
                        key={media.id}
                        href={media.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{ position: "relative", display: "block" }}
                      >
                        <img
                          src={media.thumbnail_url ?? media.url}
                          alt=""
                          style={{
                            width: "100%",
                            aspectRatio: "1",
                            objectFit: "cover",
                            borderRadius: 10,
                            border: "1px solid var(--border)",
                            display: "block",
                          }}
                        />
                        {media.kind === "video" && (
                          <span style={{ ...overlayPill, top: 6, left: 6 }}>
                            <Play size={9} fill="#fff" />
                          </span>
                        )}
                      </a>
                    ))}
                </div>
              </Section>
            )}

            <Section title="Content">
              <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--text-primary)", margin: 0 }}>
                {open.content || <em>No caption</em>}
              </p>
            </Section>

            <Section title="Details">
              <Field label="Post ID" value={open.id} mono />
              <Field label="Author ID" value={open.user_id} mono />
              <Field label="Topics" value={open.topics?.join(", ") || "—"} />
              <Field label="Hashtags" value={open.hashtags?.join(" ") || "—"} />
              <Field
                label="Engagement"
                value={`${open.likes_count} likes · ${open.comments_count} comments · ${open.saves_count} saves`}
              />
            </Section>

            <Section title={`Comments (${comments.data?.length ?? 0})`}>
              {comments.error && <ErrorState error={comments.error} onRetry={comments.refetch} />}
              {comments.loading && (
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Loading…</div>
              )}
              {comments.data?.length === 0 && !comments.loading && (
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>No comments</div>
              )}
              {comments.data?.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border)",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar name={comment.profiles?.full_name ?? null} seed={comment.user_id} size={26} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      {comment.profiles?.full_name ?? "Unknown"}
                      <span style={{ fontWeight: 500, color: "var(--text-muted)", marginLeft: 6 }}>
                        {dateTime(comment.created_at)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.55, marginTop: 2 }}>
                      {comment.content}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ border: "none", flexShrink: 0 }}
                    title="Delete comment"
                    onClick={() =>
                      setPending({
                        kind: "comment",
                        commentId: comment.id,
                        excerpt: comment.content.slice(0, 80),
                      })
                    }
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </Section>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!pending}
        busy={busy}
        destructive
        requireReason
        reasonLabel="Reason (recorded in the audit log)"
        title={pending?.kind === "comment" ? "Delete this comment?" : "Delete this post?"}
        body={
          pending?.kind === "comment" ? (
            <>Removes the comment permanently. “{pending.excerpt}…”</>
          ) : (
            <>
              Removes the post and everything attached to it — media, likes,
              saves and all {pending?.kind === "post" ? pending.post.comments_count : 0}{" "}
              comments. This can&apos;t be undone.
            </>
          )
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

const inline: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4 };

const overlayPill: React.CSSProperties = {
  position: "absolute",
  top: 10,
  left: 10,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 8px",
  borderRadius: 999,
  background: "rgba(15, 16, 30, 0.66)",
  backdropFilter: "blur(6px)",
  color: "#fff",
  fontSize: 10,
  fontWeight: 800,
};
